import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../../lib/api';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { CustomFields } from './CustomFields';

const Card = ({ children, testid }) => <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={testid}>{children}</div>;
const Sel = (props) => <select {...props} className={`border border-slate-200 rounded-md px-2 py-2 text-sm bg-white ${props.className || ''}`} />;
const BADGE = { registered: 'bg-green-100 text-green-700', waitlisted: 'bg-amber-100 text-amber-700', dropped: 'bg-slate-100 text-slate-400' };

const FeeSettingsCard = () => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['acad-fee-settings'], queryFn: () => api.get('/acad/fee-settings').then((r) => r.data) });
  const [val, setVal] = useState('');
  const save = async () => {
    try {
      await api.post('/acad/fee-settings', { fee_per_credit: parseFloat(val) || 0 });
      toast.success('Fee per credit saved'); setVal(''); qc.invalidateQueries({ queryKey: ['acad-fee-settings'] });
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card testid="fee-settings-card">
      <p className="font-display font-bold mb-1">Registration fees</p>
      <p className="text-xs text-slate-500 mb-2">Registering raises a tuition line on one invoice per student per term. A course's own tuition fee wins; otherwise fee per credit × course credits applies. Dropping in the window reverses the charge. Arrears never block academics.</p>
      <div className="flex gap-2 items-center flex-wrap">
        <span className="text-sm">Fee per credit: <b data-testid="fee-per-credit-value">{data?.fee_per_credit ?? 0}</b></span>
        <Input data-testid="fee-per-credit-input" className="w-32" type="number" placeholder="New amount" value={val} onChange={(e) => setVal(e.target.value)} />
        <Button data-testid="fee-per-credit-save" size="sm" variant="outline" disabled={val === ''} onClick={save}>Save</Button>
      </div>
    </Card>
  );
};

export const RegistrationTab = () => {
  const qc = useQueryClient();
  const { data: terms } = useQuery({ queryKey: ['acad-terms'], queryFn: () => api.get('/acad/terms').then((r) => r.data) });
  const [termId, setTermId] = useState('');
  const { data: sections } = useQuery({ queryKey: ['acad-sections-term', termId], queryFn: () => api.get('/acad/sections', { params: termId ? { term_id: termId } : {} }).then((r) => r.data) });
  const [sectionId, setSectionId] = useState('');
  const { data: roster } = useQuery({ enabled: !!sectionId, queryKey: ['acad-roster', sectionId], queryFn: () => api.get('/acad/registrations', { params: { section_id: sectionId } }).then((r) => r.data) });
  const { data: students } = useQuery({ queryKey: ['students-all'], queryFn: () => api.get('/students').then((r) => r.data).catch(() => []) });
  const [f, setF] = useState({ student_id: '', override: false, reason: '', custom: {} });
  const [statusFilter, setStatusFilter] = useState('active');

  const refresh = () => { qc.invalidateQueries({ queryKey: ['acad-roster', sectionId] }); qc.invalidateQueries({ queryKey: ['acad-sections-term'] }); };
  const registerStudent = async () => {
    try {
      const { data } = await api.post('/acad/registrations', { section_id: sectionId, student_id: f.student_id, override: f.override, reason: f.reason || null, custom: f.custom });
      const st = data.registration.status;
      const feeNote = data.fee_charged ? ` · tuition ${data.fee_charged} charged` : '';
      toast[data.warnings?.length ? 'warning' : 'success'](st === 'waitlisted' ? `Added to the waitlist at position ${data.registration.waitlist_position}` : `Registered${feeNote}`, { description: (data.warnings || []).join(' · ') || undefined });
      setF({ ...f, student_id: '', reason: '', override: false }); refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const drop = async (rid) => {
    try {
      const { data } = await api.post(`/acad/registrations/${rid}/drop`, { reason: 'Dropped by staff' });
      const bits = [data.promoted ? `${data.promoted} was promoted from the waitlist` : null, data.fee_reversed ? `tuition ${data.fee_reversed} reversed` : null].filter(Boolean).join(' · ');
      toast.success(bits ? `Dropped - ${bits}` : 'Dropped'); refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const section = (sections || []).find((s) => s.id === sectionId);
  const visibleRows = (roster || []).filter((r) => statusFilter === 'all' ? true : statusFilter === 'active' ? r.status !== 'dropped' : r.status === statusFilter);
  return (
    <div className="space-y-4">
      <Card testid="reg-picker">
        <p className="font-display font-bold mb-2">Pick a section</p>
        <div className="flex gap-2 flex-wrap">
          <Sel data-testid="reg-term" value={termId} onChange={(e) => { setTermId(e.target.value); setSectionId(''); }}>
            <option value="">All terms...</option>{(terms || []).map((t) => <option key={t.id} value={t.id}>{t.code}</option>)}
          </Sel>
          <Sel data-testid="reg-section" value={sectionId} onChange={(e) => setSectionId(e.target.value)}>
            <option value="">Section...</option>{(sections || []).map((s) => <option key={s.id} value={s.id}>{s.course_code} {s.section_code} · {s.enrolled_count}/{s.capacity} seats</option>)}
          </Sel>
        </div>
      </Card>
      {section && (
        <Card testid="reg-register">
          <p className="font-display font-bold mb-1">Register a student into {section.course_code} {section.section_code}</p>
          <p className="text-xs text-slate-500 mb-2">{section.enrolled_count}/{section.capacity} seats filled · waitlist up to {section.waitlist_capacity}. Prerequisites, windows and exclusive rules are checked automatically.</p>
          <div className="flex gap-2 flex-wrap items-center">
            <Sel data-testid="reg-student" value={f.student_id} onChange={(e) => setF({ ...f, student_id: e.target.value })}>
              <option value="">Student...</option>{(students || []).map((s) => <option key={s.id} value={s.id}>{s.name}{s.grade ? ` (Grade ${s.grade})` : ''}</option>)}
            </Sel>
            <label className="text-xs text-slate-600 flex items-center gap-1"><input data-testid="reg-override" type="checkbox" checked={f.override} onChange={(e) => setF({ ...f, override: e.target.checked })} /> Override rules</label>
            {f.override && <Input data-testid="reg-reason" className="w-64" placeholder="Reason for the override (required)" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} />}
          </div>
          <CustomFields entity="registration" value={f.custom} onChange={(custom) => setF({ ...f, custom })} />
          <Button data-testid="reg-submit" className="mt-3 bg-slate-900 hover:bg-slate-800" disabled={!f.student_id} onClick={registerStudent}>Register student</Button>
        </Card>
      )}
      {section && (
        <Card testid="reg-roster">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
            <p className="font-display font-bold">Roster and waitlist</p>
            <div className="flex gap-1.5">
              {['active', 'registered', 'waitlisted', 'dropped', 'all'].map((sfk) => (
                <button key={sfk} data-testid={`roster-filter-${sfk}`} onClick={() => setStatusFilter(sfk)}
                  className={`text-xs px-2 py-1 rounded-full ${statusFilter === sfk ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`}>{sfk}</button>
              ))}
            </div>
          </div>
          {visibleRows.map((r) => (
            <div key={r.id} className="border-b border-slate-50 py-2 flex items-center justify-between gap-2 text-sm" data-testid={`reg-row-${r.id}`}>
              <span>{r.student_name} <span className={`text-[10px] px-1.5 py-0.5 rounded ${BADGE[r.status]}`}>{r.status}{r.waitlist_position ? ` #${r.waitlist_position}` : ''}</span>
                {r.fee_charged ? <span className="text-[10px] text-slate-400 ms-1">tuition {r.fee_charged}</span> : null}</span>
              {r.status !== 'dropped' && <button data-testid={`reg-drop-${r.id}`} onClick={() => drop(r.id)} className="text-xs text-rose-500 underline">Drop</button>}
            </div>
          ))}
          {visibleRows.length === 0 && <p className="text-sm text-slate-400">Nothing matches this filter.</p>}
        </Card>
      )}
      <FeeSettingsCard />
    </div>
  );
};
